import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { AlertContent } from 'components';
import { useDebounce } from 'hooks';
import { selectIsLoadingAPIRequest } from 'store/ApiClient';
import { Employees, getEmployeesRequest, selectEmployees } from 'store/Collections';
import { getMatchEmployeesRequest, linkEmployeeRequest } from 'store/TipCalculator';
import { IEmployerState, IStore } from 'store/types';
import { AffiliationType, ApiQueryOperator, EmployeeType, IEmployee, IEmployeeIntegration, IEmployerSite, IPOSProvider, ISiteIntegration } from 'types';
import Confirm from './components/Confirm';
import Error from './components/Error';
import InviteNew from './components/InviteNew';
import Loader from './components/Loader';
import NoMatch from './components/NoMatch';
import Search from './components/Search';
import Success from './components/Success';
import SuggestedMatch from './components/SuggestedMatch';

type InviteView = 'loader' | 'error' | 'suggestedMatch' | 'integrationMatch' | 'invite' | 'linkSuccess' | 'inviteSuccess' | 'matchNotFound' | 'search' | 'confirm';

interface IConnectEmployeeModalDataProps {
  provider: IPOSProvider | null;
  integration: ISiteIntegration | null;
  site: IEmployerSite;
  organization: IEmployerState;
  onClose(): void;
  onSuccess(employee: IEmployee): void;
  dataCy?: string;
}

const ConnectEmployeeModal = ({ provider, integration, site, organization, onClose, onSuccess, dataCy = 'connect-employee-modal' }: IConnectEmployeeModalDataProps) => {
  const dispatch = useDispatch();
  const [query, setQuery] = useState(undefined);
  const searchEmployeeResults = useSelector(selectEmployees())?.data || [];
  const fetchEmployeesIsLoading = useSelector((state: IStore) => selectIsLoadingAPIRequest(state, Employees.GET_EMPLOYEES_REQUEST));

  const [view, setView] = useState<InviteView>('loader');
  const [matchEmployees, setMatchEmployees] = useState<IEmployee[]>();
  const [duplicateProviderMatchEmployee, setDuplicateProviderMatchEmployee] = useState<IEmployee>();
  const [selectedEmployee, setSelectedEmployee] = useState<IEmployee>();
  const [isLinking, setIsLinking] = useState(false);

  const showModalAfterMatchCheck = (employees: IEmployee[]) => {
    if (employees.length) {
      // Suggested employee matches found
      setMatchEmployees(employees);
      setView('suggestedMatch');
    } else {
      setQuery(undefined);
      setMatchEmployees(undefined);
      setView('matchNotFound');
    }
  };

  const showDuplicateProviderMatchCheck = (employee: IEmployee) => {
    setDuplicateProviderMatchEmployee(employee);
    setView('integrationMatch');
  };

  const showErrorModalAfterMatchCheck = () => {
    setView('error');
  };

  useEffect(() => {
    if (view === 'loader') dispatch(getMatchEmployeesRequest(site.id, provider, showModalAfterMatchCheck, showDuplicateProviderMatchCheck, showErrorModalAfterMatchCheck));
  }, [provider, site, organization, integration]);

  const onLink = (employee: IEmployee, site: IEmployerSite) => () => {
    setIsLinking(true);
    const integration: IEmployeeIntegration = { id: provider.employeeId, siteId: site.id, type: provider.type };
    dispatch(linkEmployeeRequest(employee, integration, site, onLinkSuccess, onLinkError));
  };

  const onLinkSuccess = (employee: IEmployee) => {
    onSuccess(employee);
    setIsLinking(false);
    setView('linkSuccess');
  };

  const onInviteSuccess = (employee: IEmployee) => {
    onSuccess(employee);
    setSelectedEmployee(employee);
    setIsLinking(false);
    setView('inviteSuccess');
  };

  const onLinkError = () => {
    setIsLinking(false);
    setView('error');
  };

  // Search Employees
  useEffect(() => {
    if (query) {
      dispatch(
        getEmployeesRequest(AffiliationType.SITES, site.id, {
          sort: 'name',
          limit: 0,
          q: [
            { key: 'name', operator: ApiQueryOperator.like, value: query },
            { key: 'type', operator: ApiQueryOperator.neq, value: EmployeeType.ADMIN },
          ],
        })
      );
    }
  }, [query, site]);

  const searchEmployees = (e: React.ChangeEvent<HTMLInputElement>) => {
    setQuery(e.target.value?.trim());
    setSelectedEmployee(undefined);
  };

  const onSearchChange = useDebounce(searchEmployees);

  const renderIntegrationMatch = (employee: IEmployee, site: IEmployerSite) => {
    const message = (
      <>
        <span>
          A dismissed user <b>{employee.name}</b> is already connected to the integration.
        </span>
        <br />
        <br />
        Click below to reactivate <b>{employee.name}</b> on <b>{site.name}</b>.
      </>
    );

    return <AlertContent title="Dismissed User" message={message} hideLeftAction onRightAction={onLink(employee, site)} rightActionLabel="Reactivate" isLoading={isLinking} onClose={onClose} />;
  };

  const renderError = () => <Error onClose={onClose} dataCy={dataCy} />;

  const renderContent = (onClose: () => void) => {
    if (!site) return renderError();

    switch (view) {
      case 'error':
        return renderError();
      case 'linkSuccess':
        return <Success provider={provider} onClose={onClose} title="Successfully linked!" dataCy={dataCy} />;
      case 'integrationMatch':
        return renderIntegrationMatch(duplicateProviderMatchEmployee, site);
      case 'inviteSuccess':
        return <Success provider={provider} employee={selectedEmployee} site={site} onClose={onClose} title="Successfully invited!" dataCy={dataCy} />;
      case 'invite':
        return (
          <InviteNew
            provider={provider}
            site={site}
            org={organization}
            integration={integration}
            onSecondaryAction={() => {
              setQuery(undefined);
              setView('search');
            }}
            onPrimaryAction={onInviteSuccess}
            onClose={onClose}
            dataCy={dataCy}
          />
        );
      case 'confirm':
        return <Confirm provider={provider} integration={integration} onClose={onClose} employee={selectedEmployee} site={site} isProcessing={isLinking} onConfirm={onLink(selectedEmployee, site)} dataCy={dataCy} />;
      case 'matchNotFound':
        return (
          <NoMatch
            provider={provider}
            integration={integration}
            onClose={onClose}
            onSearch={() => {
              setQuery(undefined);
              setMatchEmployees(undefined);
              setView('search');
            }}
            dataCy={dataCy}
          />
        );
      case 'suggestedMatch':
        return (
          <SuggestedMatch
            provider={provider}
            site={site}
            integration={integration}
            employees={matchEmployees}
            selectedEmployee={selectedEmployee}
            onClose={onClose}
            onContinue={() => setView('confirm')}
            onInvite={() => setView('invite')}
            onSearch={() => {
              setQuery(undefined);
              setMatchEmployees(undefined);
              setView('search');
            }}
            onSetSelectedEmployee={(employee) => setSelectedEmployee(employee)}
            dataCy={dataCy}
          />
        );
      case 'search':
        return (
          <Search
            provider={provider}
            site={site}
            integration={integration}
            onClose={onClose}
            employees={query && searchEmployeeResults}
            selectedEmployee={selectedEmployee}
            onSearchChange={onSearchChange}
            onSetSelectedEmployee={(employee) => setSelectedEmployee(employee)}
            onContinue={() => setView('confirm')}
            isLoading={fetchEmployeesIsLoading}
            onInvite={() => setView('invite')}
            dataCy={dataCy}
          />
        );
      default:
        return <Loader provider={provider} integration={integration} onClose={onClose} dataCy={dataCy} />;
    }
  };

  return renderContent(onClose);
};

export default ConnectEmployeeModal;
